'use client';

import { forwardRef, ForwardedRef, useId, useState, MouseEvent } from 'react';
import classNames from 'classnames';

import { Checkbox } from '../Checkbox';
import { Error } from '../Error';
import { Helper } from '../Helper';
import { Label } from '../Label';

import './css/Input.scss';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean | string;
  errorDescribedBy?: string;
  hasShowHide?: boolean;
  helper?: string;
  isErrorAnnounced?: boolean;
  label?: string;
  onShowHideClick?: (e: MouseEvent) => void;
  showHideLabel?: string;
}

export const Input = forwardRef(
  (props: InputProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      disabled,
      className,
      error,
      errorDescribedBy,
      hasShowHide,
      helper,
      id,
      isErrorAnnounced = true,
      onShowHideClick,
      showHideLabel,
      type = 'text',
      label,
      name,
      value,
      ...spreadProps
    } = props;

    const customInputId = `${useId()}-input`;
    const inputId = id || customInputId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const showHideId = `${inputId}-show-hide`;

    const [showHideChecked, setShowHideChecked] = useState(false);

    const errorWithNoMessage = typeof error === 'boolean';

    const getDescribedBy = () => {
      if (helper || error || errorDescribedBy) {
        const errorDescribedById = errorDescribedBy || errorId;

        return `${helper ? helperId : ''} ${
          error ? errorDescribedById : ''
        }`.trim();
      }
      return undefined;
    };

    const determineInputType = () => {
      if (hasShowHide && !showHideChecked) {
        return 'password';
      }
      // hasShowHide means the type will be type "password" some of the time, so
      // we can't use any of the built in validation types (e.g. email) or
      // things like date/time. Thus, we force hasShowHide inputs to "text"
      // whenever they're not "password"
      if (hasShowHide) {
        return 'text';
      }
      return type;
    };

    const handleShowHideClick = (e: MouseEvent) => {
      setShowHideChecked(!showHideChecked);
      if (onShowHideClick) {
        onShowHideClick(e);
      }
    };

    const inputClass = classNames({
      'ps-input': true,
      'ps-input-disabled': disabled,
      'ps-input-error': error,
      [`${className}`]: className,
    });

    return (
      <div className={inputClass}>
        {label && (
          <Label
            isBold
            disabled={disabled}
            htmlFor={inputId}
          >
            {label}
          </Label>
        )}

        {helper && (
          <Helper
            disabled={disabled}
            id={helperId}
          >
            {helper}
          </Helper>
        )}
        <input
          {...spreadProps}
          name={name}
          ref={ref}
          aria-describedby={getDescribedBy()}
          disabled={disabled}
          id={inputId}
          type={determineInputType()}
          value={value}
        ></input>

        {hasShowHide && (
          <Checkbox
            disabled={disabled}
            aria-controls={inputId}
            id={showHideId}
            label={showHideLabel || `Show ${label?.toLowerCase()}`}
            onClick={handleShowHideClick}
            checked={showHideChecked}
          ></Checkbox>
        )}

        {error && !errorWithNoMessage && !disabled && (
          <Error
            id={errorId}
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </div>
    );
  }
);
